import { env } from "cloudflare:workers"
import { redirect } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { getRequestHeader } from "@tanstack/react-start/server"
import { z } from "zod"
import { validateTurnstile } from "@/cloudflare/turnstile"
import {
  DUMMY_PASSWORD_HASH,
  hashPassword,
  MIN_PASSWORD_LENGTH,
  verifyPassword,
} from "@/crypto"
import { parseFormData } from "@/form"
import { getRedirectUrl } from "../auth.server"
import { updateAppSession } from "../session.server"
import { createUserWithPassword, getUserByEmail } from "../user.server"

export const loginFormSchema = z.object({
  email: z.email(),
  password: z.string().min(1, "Password is required"),
  mode: z.enum(["login", "register"]).optional().default("login"),
  redirectTo: z.string().optional(),
  "cf-turnstile-response": z.string().optional(),
})

function isPublicRegistrationAllowed() {
  return env.ALLOW_PUBLIC_REGISTRATION === "true"
}

export const getLoginPageData = createServerFn({ method: "GET" })
  .validator((data: { redirectTo: string }) => data)
  .handler(async ({ data }) => {
    let sisterAppUrl = ""
    try {
      const raw = (env as { SISTER_APP_URL?: string }).SISTER_APP_URL
      sisterAppUrl = raw?.trim().replace(/\/+$/, "") || ""
    } catch {
      sisterAppUrl = ""
    }
    return {
      cloudflareTurnstilePublicKey: env.CLOUDFLARE_TURNSTILE_PUBLIC_KEY,
      redirectTo: getRedirectUrl(data.redirectTo),
      allowRegistration: isPublicRegistrationAllowed(),
      /** Base URL of the other app for cross-host SSO (empty if unset). */
      sisterAppUrl,
    }
  })


async function validateTurnstileIfConfigured(token?: string) {
  const cloudflareTurnstileSecretKey = env.CLOUDFLARE_TURNSTILE_SECRET_KEY
  if (!cloudflareTurnstileSecretKey) return

  const ip = getRequestHeader("CF-Connecting-IP")
  const isHuman = await validateTurnstile({
    secret: cloudflareTurnstileSecretKey,
    token,
    ip,
  })

  if (!isHuman) {
    throw new Error("Human verification failed")
  }
}

export const loginFormAction = createServerFn({ method: "POST" })
  .validator((data: FormData) => parseFormData(loginFormSchema, data))
  .handler(async ({ data }) => {
    const {
      email: rawEmail,
      password,
      mode,
      redirectTo,
      "cf-turnstile-response": turnstileResponse,
    } = data

    const email = rawEmail.trim().toLowerCase()

    await validateTurnstileIfConfigured(turnstileResponse)

    if (password.length < MIN_PASSWORD_LENGTH) {
      return {
        error: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
        mode,
        email,
      }
    }

    if (mode === "register") {
      if (!isPublicRegistrationAllowed()) {
        return {
          error: "Registration is disabled. Contact an administrator.",
          mode: "login" as const,
          email,
        }
      }

      const existing = await getUserByEmail(email)
      if (existing) {
        return {
          error: "An account with this email already exists. Sign in instead.",
          mode: "register" as const,
          email,
        }
      }

      const passwordHash = await hashPassword(password)
      const user = await createUserWithPassword(email, passwordHash)
      await updateAppSession({ userId: `${user.id}` })
      throw redirect({ to: getRedirectUrl(redirectTo) })
    }

    // Always run PBKDF2 (dummy hash when user missing) to reduce timing leaks.
    const user = await getUserByEmail(email)
    const passwordOk = await verifyPassword(
      password,
      user?.passwordHash ?? DUMMY_PASSWORD_HASH,
    )

    if (!user || !passwordOk) {
      return {
        error: "Invalid email or password",
        mode: "login" as const,
        email,
      }
    }

    await updateAppSession({ userId: `${user.id}` })
    throw redirect({ to: getRedirectUrl(redirectTo) })
  })
